import { each, find, flatten, map, some } from 'lodash-es';

/**
 * Iterate through all the products one at a time. The order is incumbent, first offer's crosses, second offer's crosses, etc.
 * Then the next incumbent product and so on.
 *
 * @param products
 * @yields A value containing the product (and the incumbent which goes with it if it is not the incumbent), the calculations
 * for this product, and whether it's visible or not based upon the current filters.
 */
export function* allProducts(
  products: any[],
  offerId: number | string | null = null
) {
  for (let product of products) {
    // Delegate to another generator to yield each product in turn.
    yield* incumbentAndCrosses(product, offerId);
  }
}

/**
 * The same as above, except that you only get the incumbent and crosses for filtered products rather than all of them.
 *
 * @param products
 */
export function* filteredProducts(
  products: any[],
  offerId: number | string | null = null
) {
  for (let product of products) {
    if (product.visible) {
      // Delegate to another generator to yield each product in turn.
      yield* incumbentAndCrosses(product, offerId);
    }
  }
}

/**
 * Another iterator of all the products except that only the incumbents are returned.
 *
 * @param products
 */
export function* filteredIncumbentProducts(products: any[]) {
  for (let product of products) {
    if (product.visible) {
      // Delegate to another generator to return the incumbent in the correct format.
      yield* justIncumbent(product);
    }
  }
}

export function* filteredCrossedProducts(
  products: any[],
  offerId: number | string | null = null
) {
  for (let product of products) {
    if (product.visible) {
      // Delegate to another generator.
      yield* justCrosses(product, offerId);
    }
  }
}

export function* justIncumbent(product: any) {
  yield {
    calculations: product.calculations,
    offerId: 'incumbent',
    product: product.incumbentProduct,
    visible: product.visible,
    discontinued: product.discontinued,
    unpriced: product.unpriced,
    discontinuedCrossed: product.discontinuedCrossed,
    unpricedCrossed: product.unpricedCrossed,
    notDiscontinuedCrossed: product.notDiscontinuedCrossed,
    incumbentSelfCross: product.incumbentSelfCross,
  };
}

/**
 * Given a product from the back-end, this will yield first the incumbent product and then each cross in turn (by offer).
 *
 * @param product A single product as received from the back-end.
 */
export function* incumbentAndCrosses(
  product: any,
  offerId: number | string | null = null
) {
  yield* justIncumbent(product);

  yield* justCrosses(product, offerId);
}

/**
 * Given a product from the back-end, this will return each of the crossed products in turn (by offer).
 *
 * @param product A single product as received from the back-end.
 */
export function* justCrosses(
  product: any,
  offerId: number | string | null = null
) {
  for (let offer of product.crossedProductsByOffer) {
    if (offerId === null || (offerId !== null && offer.offerId === offerId)) {
      for (let cross of offer.crossedProducts) {
        yield {
          calculations: product.calculations,
          incumbent: product.incumbentProduct,
          offerId: offer.offerId,
          product: cross,
          visible: product.visible,
          discontinued: product.discontinued,
          unpriced: product.unpriced,
          discontinuedCrossed: product.discontinuedCrossed,
          unpricedCrossed: product.unpricedCrossed,
          notDiscontinuedCrossed: product.notDiscontinuedCrossed,
          incumbentSelfCross: product.incumbentSelfCross,
        };
      }
    }
  }
}

export function getCoverageByOffer(
  unspsc: any,
  offerId: number | string | null
): any {
  return find(unspsc.coverageByOffer, { offerId: offerId });
}

export function findIncumbentProduct(
  state: any,
  incumbentProductId: number
): any {
  // Search through the products until we find the incumbent product in question.
  let product = state.products.find((product: any) => {
    if (product.incumbentProduct.productId === incumbentProductId) {
      return true;
    } else {
      return false;
    }
  });

  return product;
}

export function findProductCross(
  state: any,
  incumbentProductId: number,
  crossReferenceId: number
) {
  let inCumbentProduct = findIncumbentProduct(state, incumbentProductId);

  return findCrossByCrossReferenceId(inCumbentProduct, crossReferenceId);
}

export function findCrossedProductsByOffer(product: any, offerId: number): any {
  return find(product.crossedProductsByOffer, { offerId: offerId });
}

// Returns null if no cross can be found.
export function findCross(product: any, productId: number): any {
  let cross;

  each(product.crossedProductsByOffer, (offer: any) => {
    cross = find(offer.crossedProducts, { productId: productId });

    if (cross) {
      return false;
    }
  });

  return cross == null ? null : cross;
}

export function findCrossByCrossReferenceId(
  product: any,
  crossReferenceId: number
): any {
  let cross;

  each(product.crossedProductsByOffer, (offer: any) => {
    cross = find(offer.crossedProducts, { crossReferenceId: crossReferenceId });

    if (cross) {
      return false;
    }
  });

  return cross == null ? null : cross;
}

export function getAllocations(product: any): any[] {
  const allocations = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        // Only add in the UNSPSC ID if we have it.
        let unspscId = crossedProduct.unspsc ? crossedProduct.unspsc.id : null;

        return {
          productId: crossedProduct.productId,
          unspscId,
          allocationPct: crossedProduct.allocation
        };
      });
    })
  );

  // Look to see if we can find the incumbent product ID in the list of allocations. If it's there
  // we don't need to add the incumbent product to the list.
  if (!find(allocations, { productId: product.incumbentProduct.productId })) {
    // Only add in the UNSPSC ID if we have it.
    let unspscId = product.incumbentProduct.unspsc
      ? product.incumbentProduct.unspsc.id
      : null;

    allocations.push({
      productId: product.incumbentProduct.productId,
      unspscId,
      allocationPct: product.incumbentProduct.allocation
    });
  }

  return allocations;
}

export function isSelfCrossed(product: any) {
  let foundSelfCross = false;
  each(product.crossedProductsByOffer, (offer: any) => {
    if (
      find(offer.crossedProducts, {
        productId: product.incumbentProduct.productId
      })
    ) {
      foundSelfCross = true;
    }
  });

  return foundSelfCross;
}

export function isLocked(product: any): boolean {
  const locked = product.incumbentProduct.lockedAllocation;

  const lockStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.lockedAllocation;
      });
    })
  );

  return some(lockStatus) || locked;
}

export function isLockedByProductId(state: any, incumbentProductId: number) {
  const product = findIncumbentProduct(state, incumbentProductId);

  return isLocked(product);
}

export function isDiscontinued(product: any): boolean {
  const discontinued = product.incumbentProduct.productStatus === 'Discontinued';

  const discontinuedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.productStatus === 'Discontinued';
      });
    })
  );

  return some(discontinuedStatus) || discontinued;
}

export function isDiscontinuedCrossed(product: any): boolean {

  const discontinuedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.productStatus === 'Discontinued';
      });
    })
  );

  return some(discontinuedStatus)
}

export function isNotDiscontinuedCrossed(product: any): boolean {

  const notDiscontinuedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.productStatus !== 'Discontinued';
      });
    })
  );

  return some(notDiscontinuedStatus)
}

export function isUnpriced(product: any): boolean {
  const unpriced = product.incumbentProduct.unitPrice === null;

  const unpricedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.unitPrice === null || crossedProduct.usedIncumbentPrice === true;
      });
    })
  );

  return some(unpricedStatus) || unpriced;
}

export function isUnpricedCrossed(product: any): boolean {

  const unpricedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.unitPrice === null || crossedProduct.usedIncumbentPrice === true;
      });
    })
  );

  return some(unpricedStatus);
}

export function isExcluded(product: any): boolean {
  const excluded = product.incumbentProduct.spendStatus === 'CARVE_IN' || product.incumbentProduct.spendStatus === 'CARVE_OUT';
  const excludedStatus = flatten(
    map(product.crossedProductsByOffer, (offer: any) => {
      return map(offer.crossedProducts, (crossedProduct: any) => {
        return crossedProduct.spendStatus === 'CARVE_IN' || crossedProduct.spendStatus === 'CARVE_OUT';
      });
    })
  );

  return some(excludedStatus) || excluded
}


// check if single product is conversion or not.
export function isConversionProduct(product: any): boolean {
  return (
    product.allocation > 0 && product.productId !== product.incumbentProductId
  );
}

export function isIncumbentProduct(product: any): boolean {
  return product.productId === product.incumbentProductId;
}

export function isIncumbentSelfCross(product: any): boolean {

  return product.incumbentProduct.allocation > 0 && product.crossedProductsByOffer.length === 0;
}
